import { useContext, useCallback } from 'react';
import { Box, Button, styled, Typography, Link } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import MicrosoftLogo from '@/UI/Icons/MicrosoftLogo';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AuthContext } from '@/contexts/AuthContext';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { DescopeSignInButtons } from '@/components/Common/Authentication/DescopeSignInButtons';
import { AUTH_PROVIDERS, DESCOPE_FLOW_IDS } from '@/consts/auth.consts';
import { FF_TERMS_OF_SERVICE } from '@/consts/featureFlags';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { ACTIVATION_QUERY_PARAMS_KEY } from '@/pages/TeamInviteActivationPage';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import type { SignInOptions } from '@/contexts/AuthContext';

const StyledSignInButton = styled(Button)(() => ({
  alignItems: 'center',
  borderRadius: '8px',
  width: '240px',
  backgroundColor: 'transparent',
  border: `1px solid rgba(242, 240, 248, 0.5)`,
  color: color.White100,
  display: 'flex',
  height: '42px',
  gap: '8px',

  '&:hover': {
    backgroundColor: color.Black84,
  },
}));

const FirebaseSignInButtons = ({
  signIn,
  from,
  promotion,
  teamInviteToken,
  hintEmail,
  microsoftTenantId,
  initiateRedirectSignIn,
}: {
  signIn: (options: SignInOptions) => Promise<void>;
  from: string;
  promotion: string | null;
  teamInviteToken?: string | null;
  hintEmail?: string | null;
  microsoftTenantId?: string;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { value: isTermsOfServiceEnabled } = useFeatureFlag(FF_TERMS_OF_SERVICE);

  const googleSignIn = () =>
    void signIn({ route: from, providerType: 'google', teamInviteToken, hintEmail, promotion });
  const microsoftSignIn = () =>
    void signIn({ route: from, providerType: 'microsoft', teamInviteToken, hintEmail, microsoftTenantId, promotion });
  const figmaSignIn = useCallback(() => {
    const currentSearch = window.location.search;
    if (currentSearch) {
      localStorage.setItem(ACTIVATION_QUERY_PARAMS_KEY, currentSearch);
    }
    void initiateRedirectSignIn('figma', window.location.pathname);
  }, [initiateRedirectSignIn]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isTermsOfServiceEnabled && (
        <Typography
          variant="body-sm-rg"
          color={color.White100}
          sx={{ mb: 3, textAlign: 'center', maxWidth: '234px', fontSize: '10px' }}
        >
          {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_TEXT)}{' '}
          <Link
            href={EXTERNAL_LINKS.termsOfService}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
          >
            {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_OF_SERVICE_LINK)}
          </Link>{' '}
          and{' '}
          <Link
            href={EXTERNAL_LINKS.privacyPolicy}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
          >
            {t(I18N_KEYS.SIGN_IN_PAGE.PRIVACY_POLICY_LINK)}
          </Link>
          .
        </Typography>
      )}
      <StyledSignInButton variant="contained" onClick={googleSignIn}>
        <GoogleLogo width={24} height={24} title="Google logo" />
        <Typography variant="body-std-rg" color={color.White100}>
          {t(I18N_KEYS.SIGN_IN_PAGE.GOOGLE_SIGN_IN_BUTTON)}
        </Typography>
      </StyledSignInButton>
      <StyledSignInButton variant="contained" onClick={microsoftSignIn}>
        <MicrosoftLogo width={24} height={24} title="Microsoft logo" />
        <Typography variant="body-std-rg" color={color.White100}>
          {t(I18N_KEYS.SIGN_IN_PAGE.MICROSOFT_SIGN_IN_BUTTON)}
        </Typography>
      </StyledSignInButton>
      <StyledSignInButton variant="contained" onClick={figmaSignIn}>
        <FigmaLogo width={24} height={24} title="Figma logo" />
        <Typography variant="body-std-rg" color={color.White100}>
          {t(I18N_KEYS.SIGN_IN_PAGE.FIGMA_SIGN_IN_BUTTON)}
        </Typography>
      </StyledSignInButton>
    </Box>
  );
};

// pass token and email and remove form url when enter this one
export function TeamInviteSignin({
  fullScreen = true,
  teamInviteToken,
  hintEmail,
  microsoftTenantId,
}: {
  fullScreen?: boolean;
  teamInviteToken?: string | null;
  hintEmail?: string | null;
  microsoftTenantId?: string;
}) {
  const [searchParams] = useSearchParams();
  const promotion = searchParams.get('promotion');
  const { provider } = useAuthConfig();
  const isDescope = provider === AUTH_PROVIDERS.DESCOPE;

  const from = '/';
  const { signIn, onSignInSuccess, onSignInError, initiateRedirectSignIn } = useContext(AuthContext);

  return (
    <Box
      sx={{
        width: fullScreen ? '100%' : 'auto',
        height: fullScreen ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {isDescope ? (
        <DescopeSignInButtons
          flowId={DESCOPE_FLOW_IDS.MEMBERS_INVITE_SCREEN}
          onSignInSuccess={onSignInSuccess}
          onSignInError={onSignInError}
          promotion={promotion}
          from={from}
          teamInviteToken={teamInviteToken}
        />
      ) : (
        <FirebaseSignInButtons
          signIn={signIn}
          from={from}
          promotion={promotion}
          teamInviteToken={teamInviteToken}
          hintEmail={hintEmail}
          microsoftTenantId={microsoftTenantId}
          initiateRedirectSignIn={initiateRedirectSignIn}
        />
      )}
    </Box>
  );
}
